import MfaCodeField from '@/components/Mfa/MfaCodeField';
import {
  MfaMethod,
  useMfaAuthenticationOptionsMutation,
  useVerifyMfaMutation,
} from '@/generated/graphql';
import { useLayoutStyles } from '@/hooks/styles';
import { graphQLErrorCode } from '@/utils/apollo-errors';
import { isCompleteCode } from '@/utils/mfa';
import { MFA_ERROR_CODES, mfaErrorMessage } from '@/utils/mfa-errors';
import {
  MFA_METHOD_SWITCH_LABELS as SWITCH_LABELS,
  usableMfaMethods,
} from '@/utils/mfa-methods';
import { passkeyErrorMessage } from '@/utils/passkeys';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  LinearProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import VpnKeyOutlinedIcon from '@material-ui/icons/VpnKeyOutlined';
import { Alert } from '@material-ui/lab';
import { startAuthentication } from '@simplewebauthn/browser';
import React, { useRef, useState } from 'react';

const useStyles = makeStyles((theme) => ({
  actions: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  trust: {
    // The checkbox lines up with the top line of the two-line label.
    alignItems: 'flex-start',
    margin: theme.spacing(0, 0, 2, -1.5),
    '& .MuiCheckbox-root': {
      marginTop: theme.spacing(-1),
    },
  },
  trustHint: {
    ...theme.typography.body2,
    display: 'block',
    color: theme.palette.text.secondary,
  },
  others: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    // Right of the main button; under it, left-aligned, on a phone.
    marginLeft: 'auto',
    [theme.breakpoints.down('xs')]: {
      marginLeft: theme.spacing(-1),
    },
  },
}));

interface SignInMfaStepProps {
  /** The challengeId from authenticate's auth:mfa_required error. */
  challengeId?: string;
  /** The `methods` from the same error: what this account can use. */
  methods?: MfaMethod[] | null;
  email?: string;
  /**
   * From the same error: the account may trust this browser. The API sends
   * false for every user since P-194, so the checkbox does not show.
   */
  trustDeviceAllowed?: boolean;
  /** The challenge is gone; the password must be entered again. */
  onRestart: (message?: string) => void;
  /** The second step passed and the session is signed in. */
  onVerified?: () => void;
}

export default function SignInMfaStep({
  challengeId,
  methods,
  email,
  trustDeviceAllowed = false,
  onRestart,
  onVerified,
}: SignInMfaStepProps): JSX.Element {
  const classes = useStyles();
  const layoutClasses = useLayoutStyles();
  const [verifyMfa] = useVerifyMfaMutation();
  const [mfaAuthenticationOptions] = useMfaAuthenticationOptionsMutation();
  const [usable] = useState(() => usableMfaMethods(methods));
  const [method, setMethod] = useState<MfaMethod>(
    usable[0] ?? MfaMethod.RecoveryCode,
  );
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const passkey = method === MfaMethod.Webauthn;
  const recovery = method === MfaMethod.RecoveryCode;
  const kind = recovery ? 'recovery' : 'totp';
  const complete = isCompleteCode(kind, code);
  const others = usable.filter((m) => m !== method);

  function switchMethod(next: MfaMethod) {
    setMethod(next);
    setCode('');
    setError('');
  }

  /** Sends one verifyMfa; false when it failed and the step stays. */
  async function verify(
    input: { code?: string; credential?: unknown },
    message: (err: unknown) => string,
  ): Promise<boolean> {
    try {
      await verifyMfa({
        variables: {
          input: {
            method,
            challengeId,
            ...input,
            ...(trustDevice && { trustDevice: true }),
          },
        },
        update: (cache) => {
          cache.evict({ fieldName: 'viewer', broadcast: false });
        },
      });
      // WithoutUserFrame sees the viewer and follows returnUrl. Stay in the
      // submitting state until then, so the step cannot run twice.
      onVerified?.();
      return true;
    } catch (err) {
      if (graphQLErrorCode(err) === MFA_ERROR_CODES.challengeInvalid) {
        onRestart(mfaErrorMessage(err));
        return true;
      }

      setError(message(err));
      return false;
    }
  }

  async function submit(value: string) {
    if (submitting) {
      return;
    }

    setSubmitting(true);
    setError('');

    const done = await verify({ code: value }, (err) =>
      mfaErrorMessage(err, { recoveryCode: recovery }),
    );

    if (!done) {
      setCode('');
      inputRef.current?.focus();
      setSubmitting(false);
    }
  }

  async function verifyWithPasskey() {
    if (submitting) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const { data } = await mfaAuthenticationOptions({
        variables: { challengeId },
      });
      const credential = await startAuthentication({
        optionsJSON: data?.mfaAuthenticationOptions,
      });

      if (await verify({ credential }, passkeyErrorMessage)) {
        return;
      }
    } catch (err) {
      if (graphQLErrorCode(err) === MFA_ERROR_CODES.challengeInvalid) {
        onRestart(mfaErrorMessage(err));
        return;
      }

      setError(passkeyErrorMessage(err));
    }

    setSubmitting(false);
  }

  let subheader = `Enter the 6-digit code from your authenticator app${
    email ? ` for ${email}` : ''
  }.`;
  if (passkey) {
    subheader = `Use your passkey${
      email ? ` for ${email}` : ''
    }. Your device asks for your fingerprint, face, screen lock or security key.`;
  } else if (recovery) {
    subheader =
      'Enter one of the recovery codes you saved when you set up two-step verification.';
  }

  const switches = others.length > 0 && (
    <div className={classes.others}>
      {others.map((other) => (
        <Button
          key={other}
          variant="text"
          onClick={() => {
            switchMethod(other);
            inputRef.current?.focus();
          }}
        >
          {SWITCH_LABELS[other]}
        </Button>
      ))}
    </div>
  );

  function handleChange(value: string) {
    setCode(value);

    // A full TOTP code submits itself, as authenticator apps expect.
    if (!recovery && isCompleteCode(kind, value)) {
      submit(value);
    }
  }

  const trustCheckbox = trustDeviceAllowed && (
    <FormControlLabel
      className={classes.trust}
      control={
        <Checkbox
          color="primary"
          checked={trustDevice}
          onChange={(event) => setTrustDevice(event.target.checked)}
        />
      }
      label={
        <>
          Trust this browser for 30 days
          <span className={classes.trustHint}>
            Skip this step here next time. Not on a shared computer.
          </span>
        </>
      }
    />
  );

  return (
    <>
      {Boolean(error) && (
        <Box mb={2}>
          <Alert severity="error" variant="outlined" role="alert">
            {error}
          </Alert>
        </Box>
      )}
      <Card className={layoutClasses.card} variant="outlined">
        <div className={layoutClasses.progress}>
          {submitting && <LinearProgress />}
        </div>
        <CardHeader title="Two-Step Verification" subheader={subheader} />
        <CardContent>
          {passkey ? (
            <>
              {trustCheckbox}
              <div className={classes.actions}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<VpnKeyOutlinedIcon />}
                  disabled={submitting}
                  onClick={verifyWithPasskey}
                  autoFocus
                >
                  Use a passkey
                </Button>
                {switches}
              </div>
            </>
          ) : (
            <form
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                if (complete) {
                  submit(code);
                }
              }}
            >
              <Box mb={2}>
                <MfaCodeField
                  key={method}
                  kind={kind}
                  value={code}
                  onChange={handleChange}
                  inputRef={inputRef}
                  error={Boolean(error) && !code}
                  autoFocus
                />
              </Box>
              {trustCheckbox}
              <div className={classes.actions}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={submitting || !complete}
                  type="submit"
                >
                  Verify
                </Button>
                {switches}
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </>
  );
}
